import React, { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, Loader2, X, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { fmtINR, longDate, shortDate } from "../lib/format";
import { useToast } from "../hooks/use-toast";

function fmtIdentityValue(v, unit) {
  if (unit === "UNITS") {
    return Number(v || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  }
  return fmtINR(v, { decimals: 2 });
}

/**
 * Slim red banner that appears at the top of the Dashboard when any
 * reconciliation identity is out of balance. Click → opens drilldown modal.
 *
 * Props:
 *   reconciliations  – array from /api/ledger/reconciliation
 *   allBalanced      – boolean
 *   hideWhenBalanced – default true; pass false to render an emerald "all clear"
 *   onLearnMoreHref  – default "/ledger"
 */
export default function DriftBanner({
  reconciliations,
  allBalanced,
  hideWhenBalanced = true,
  onLearnMoreHref = "/ledger",
}) {
  const [drillId, setDrillId] = useState(null);
  if (!reconciliations || reconciliations.length === 0) return null;
  if (allBalanced && hideWhenBalanced) return null;

  const offenders = (reconciliations || []).filter((r) => !r.balanced);

  if (allBalanced) {
    return (
      <div
        data-testid="drift-banner-clear"
        className="mb-5 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 flex items-center gap-3"
      >
        <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
          <span className="text-emerald-300 text-xs">✓</span>
        </div>
        <div className="text-xs text-emerald-200 font-mono">
          Books are internally consistent — all 3 reconciliation identities balanced.
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        data-testid="drift-banner"
        className="mb-5 rounded-xl border border-rose-500/40 bg-gradient-to-br from-rose-500/15 via-rose-500/5 to-transparent px-4 py-4"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-9 h-9 rounded-lg bg-rose-500/20 border border-rose-500/40 flex items-center justify-center shrink-0">
            <AlertTriangle size={16} className="text-rose-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-rose-100">
              Ledger drift detected ·{" "}
              <span className="text-rose-300 font-mono">
                {offenders.length} of {reconciliations.length} identities
              </span>{" "}
              out of balance
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {offenders.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setDrillId(r.id)}
                  data-testid={`drift-banner-chip-${r.id}`}
                  className="group inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 transition-colors text-xs font-mono"
                >
                  <span className="text-rose-200 uppercase tracking-wider text-[10px]">
                    {r.label.replace(" reconciliation", "")}
                  </span>
                  <span className="text-rose-100 tabular-nums">
                    {r.drift > 0 ? "+" : r.drift < 0 ? "−" : ""}
                    {fmtIdentityValue(Math.abs(r.drift), r.unit)}
                  </span>
                  <ChevronRight
                    size={12}
                    className="text-rose-400 group-hover:translate-x-0.5 transition-transform"
                  />
                </button>
              ))}
            </div>
            <div className="mt-2 text-[11px] text-rose-300/70 font-mono">
              Click an identity to see the offending entries.{" "}
              <Link
                to={onLearnMoreHref}
                className="underline-offset-2 hover:underline text-rose-200 inline-flex items-center gap-1"
              >
                Open Ledger <ExternalLink size={10} />
              </Link>
            </div>
          </div>
        </div>
      </div>
      {drillId && (
        <DriftDrilldownDialog
          identity={drillId}
          recon={offenders.find((r) => r.id === drillId)}
          onClose={() => setDrillId(null)}
        />
      )}
    </>
  );
}

/**
 * Modal that shows the journals + orphan entries contributing to drift
 * on a single identity. Loads its data on mount.
 */
export function DriftDrilldownDialog({ identity, recon, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (!identity) return;
    setLoading(true);
    api
      .ledgerReconciliationDrilldown(identity)
      .then(setData)
      .catch((e) => {
        toast({
          title: "Drilldown failed",
          description: e?.response?.data?.detail || "Could not load",
          variant: "destructive",
        });
        onClose?.();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);

  const unit = data?.unit || "INR";
  const label = recon?.label || `${(identity || "").toUpperCase()} reconciliation`;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="drift-drilldown-overlay"
    >
      <div
        className="w-full max-w-4xl bg-slate-900 border border-slate-800 rounded-xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-800 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-rose-300">
              Drift drilldown
            </div>
            <div className="text-base md:text-lg font-display text-white mt-0.5">{label}</div>
            {recon && (
              <div className="mt-1 text-[11px] text-slate-400 font-mono">
                Drift{" "}
                <span className="text-rose-300 tabular-nums">
                  {recon.drift > 0 ? "+" : "−"}
                  {fmtIdentityValue(Math.abs(recon.drift), recon.unit)}
                </span>{" "}
                · tolerance ±
                {recon.unit === "UNITS" ? recon.tolerance : `₹${recon.tolerance}`}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="drift-drilldown-close"
            className="text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-slate-800"
          >
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="px-5 py-10 flex items-center gap-2 text-slate-400 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading offending entries…
          </div>
        ) : (
          <DrilldownBody data={data} unit={unit} />
        )}
      </div>
    </div>
  );
}

function DrilldownBody({ data, unit }) {
  if (!data) return null;
  const { journals = [], orphans = [], totalOffendingJournals, totalOrphanEntries } = data;
  const hasAny = journals.length > 0 || orphans.length > 0;

  return (
    <div className="px-5 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
      <div className="flex gap-3 flex-wrap text-[11px] font-mono uppercase tracking-wider">
        <span className="px-2.5 py-1 rounded-md border border-rose-500/30 bg-rose-500/10 text-rose-200">
          Offending journals · {totalOffendingJournals}
        </span>
        <span className="px-2.5 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-200">
          Orphan entries · {totalOrphanEntries}
        </span>
      </div>

      {!hasAny && (
        <div className="px-4 py-10 text-center text-sm text-slate-400 border border-slate-800 border-dashed rounded-lg">
          No offending entries found. The drift may be from rounding only — open the Ledger and
          inspect totals directly.
        </div>
      )}

      {journals.length > 0 && (
        <section>
          <div className="text-xs font-semibold text-white mb-2 uppercase tracking-wider">
            Offending journals
          </div>
          <div className="space-y-3">
            {journals.map((j) => (
              <JournalCard key={j.journalId} journal={j} unit={unit} />
            ))}
          </div>
        </section>
      )}

      {orphans.length > 0 && (
        <section>
          <div className="text-xs font-semibold text-white mb-2 uppercase tracking-wider">
            Orphan entries (no journal)
          </div>
          <div className="bg-slate-950 border border-slate-800 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-900 text-slate-400 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Account</th>
                  <th className="text-left px-3 py-2">Side</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {orphans.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-900/60">
                    <td className="px-3 py-2 text-slate-300 font-mono whitespace-nowrap">
                      {shortDate(e.date)}
                    </td>
                    <td className="px-3 py-2 text-slate-200">{e.accountName}</td>
                    <td className="px-3 py-2 text-slate-400">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] border ${
                          e.sideKey === "left"
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                            : "border-rose-500/30 bg-rose-500/10 text-rose-300"
                        }`}
                      >
                        {e.sideKey}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-300 uppercase text-[10px] font-mono">
                      {e.type}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-100 font-mono tabular-nums">
                      {fmtIdentityValue(e.amount, unit)}
                    </td>
                    <td className="px-3 py-2 text-slate-400 max-w-[260px] truncate">
                      {e.description || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function JournalCard({ journal, unit }) {
  const drift = journal.drift || 0;
  return (
    <div className="border border-rose-500/30 bg-rose-500/5 rounded-lg overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between flex-wrap gap-2 border-b border-rose-500/20">
        <div className="min-w-0">
          <div className="text-xs text-slate-200 truncate">
            {journal.journalDescription || journal.journalId}
          </div>
          <div className="text-[10px] text-slate-500 font-mono mt-0.5">
            {longDate(journal.date)} · journal {journal.journalId}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Drift</div>
          <div className="text-sm font-mono tabular-nums text-rose-200">
            {drift > 0 ? "+" : "−"}
            {fmtIdentityValue(Math.abs(drift), unit)}
          </div>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-slate-900/60 text-slate-400 uppercase tracking-wider text-[10px]">
          <tr>
            <th className="text-left px-3 py-2">Account</th>
            <th className="text-left px-3 py-2">Side</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-right px-3 py-2">Amount</th>
            <th className="text-right px-3 py-2">Δ</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/70">
          {journal.entries.map((e) => (
            <tr key={e.id} className="hover:bg-slate-900/40">
              <td className="px-3 py-1.5 text-slate-200">{e.accountName}</td>
              <td className="px-3 py-1.5">
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] border ${
                    e.sideKey === "left"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                      : "border-rose-500/30 bg-rose-500/10 text-rose-300"
                  }`}
                >
                  {e.sideKey}
                </span>
              </td>
              <td className="px-3 py-1.5 text-slate-400 uppercase text-[10px] font-mono">
                {e.type}
              </td>
              <td className="px-3 py-1.5 text-right text-slate-100 font-mono tabular-nums">
                {fmtIdentityValue(e.amount, unit)}
              </td>
              <td className="px-3 py-1.5 text-right text-slate-300 font-mono tabular-nums">
                {e.signedDelta > 0 ? "+" : ""}
                {fmtIdentityValue(e.signedDelta, unit)}
              </td>
            </tr>
          ))}
          <tr className="bg-slate-950 font-medium">
            <td className="px-3 py-1.5 text-slate-400 text-[10px] uppercase tracking-wider">
              Net delta
            </td>
            <td colSpan={2}></td>
            <td className="px-3 py-1.5 text-right text-emerald-300 font-mono tabular-nums">
              {fmtIdentityValue(journal.leftDelta, unit)}
            </td>
            <td className="px-3 py-1.5 text-right text-rose-300 font-mono tabular-nums">
              {fmtIdentityValue(journal.rightDelta, unit)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Small standalone button intended for the Ledger's ReconciliationPanel rows.
// Renders only when the identity has drift; opens the same modal.
export function DriftDrillButton({ recon, className = "" }) {
  const [open, setOpen] = useState(false);
  if (!recon || recon.balanced) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`drift-drill-${recon.id}`}
        className={`inline-flex items-center gap-1 text-[10px] text-rose-300 hover:text-rose-200 underline-offset-2 hover:underline font-mono uppercase tracking-wider ${className}`}
      >
        Show offending entries <ChevronRight size={10} />
      </button>
      {open && (
        <DriftDrilldownDialog
          identity={recon.id}
          recon={recon}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
